import React, { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styles from './JsonSettingsEditor.module.css';
import {
    Plus,
    Pencil,
    Trash2,
    Check,
    X,
    ChevronRight,
    Braces,
    Brackets,
    Type,
    Hash,
    ToggleLeft,
    Home,
    Code,
    List,
    Copy,
    ClipboardPaste,
    CheckCircle2,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Any valid JSON value. */
type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/** What kind of JSON value we're looking at. */
type JsonType = 'string' | 'number' | 'boolean' | 'null' | 'array' | 'object';

/** Represents one key/value entry inside an object or one index inside an array. */
type JsonEntry = {
    key: string;
    value: JsonValue;
    type: JsonType;
};

/** A breadcrumb segment used for navigation. */
type BreadcrumbSegment = {
    label: string;
    path: (string | number)[];
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type JsonSettingsEditorProps = {
    /** If true the editor renders as a read-only viewer. */
    visualizeOnly?: boolean;
    style?: CSSProperties;
    /** The current JSON value serialised as a string. */
    settings: string;
    children?: ReactNode;
    className?: string;
    /** Enables inline editing, add & delete controls. */
    editable?: boolean;
    /** Called whenever the data changes. Receives the full root value. */
    onDataChange: (data: any) => void;
    /** Reports whether an add/edit row still needs to be confirmed or cancelled. */
    onPendingChange?: (hasPendingChange: boolean) => void;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const detectType = (v: JsonValue): JsonType => {
    if (v === null) return 'null';
    if (Array.isArray(v)) return 'array';
    return typeof v as JsonType;
};

const typeIcon = (t: JsonType, size = 14) => {
    switch (t) {
        case 'object': return <Braces size={size} />;
        case 'array': return <Brackets size={size} />;
        case 'string': return <Type size={size} />;
        case 'number': return <Hash size={size} />;
        case 'boolean': return <ToggleLeft size={size} />;
        default: return <Type size={size} />;
    }
};

const typeBadgeColor = (t: JsonType): string => {
    switch (t) {
        case 'object': return 'var(--info)';
        case 'array': return 'var(--accent)';
        case 'string': return 'var(--text-secondary)';
        case 'number': return 'var(--warning, #c9a43e)';
        case 'boolean': return 'var(--success)';
        default: return 'var(--text-tertiary)';
    }
};

const defaultForType = (t: JsonType): JsonValue => {
    switch (t) {
        case 'string': return '';
        case 'number': return 0;
        case 'boolean': return false;
        case 'null': return null;
        case 'array': return [];
        case 'object': return {};
    }
};

/** Immutably set a value at a path inside a root value. */
const setAtPath = (root: JsonValue, path: (string | number)[], value: JsonValue): JsonValue => {
    if (path.length === 0) return value;
    const [head, ...rest] = path;
    if (Array.isArray(root)) {
        const clone = [...root];
        clone[head as number] = setAtPath(clone[head as number], rest, value);
        return clone;
    }
    if (typeof root === 'object' && root !== null) {
        return { ...root, [head]: setAtPath((root as any)[head], rest, value) };
    }
    return root;
};

/** Immutably delete a key/index at a path inside a root value. */
const deleteAtPath = (root: JsonValue, path: (string | number)[], key: string | number): JsonValue => {
    const target = getAtPath(root, path);
    let updated: JsonValue;
    if (Array.isArray(target)) {
        updated = target.filter((_, i) => i !== key);
    } else if (typeof target === 'object' && target !== null) {
        const { [key as string]: _, ...rest } = target as { [k: string]: JsonValue };
        updated = rest;
    } else {
        return root;
    }
    return path.length === 0 ? updated : setAtPath(root, path, updated);
};

/** Get the value at a given path. */
const getAtPath = (root: JsonValue, path: (string | number)[]): JsonValue => {
    let current: any = root;
    for (const segment of path) {
        if (current == null) return undefined as any;
        current = current[segment];
    }
    return current;
};

/** Common property names that make good labels for objects. */
const LABEL_KEYS = ['name', 'Name', 'title', 'Title', 'label', 'Label', 'id', 'Id', 'ID'];

/** Try to extract a short human-readable label from an object. */
const objectLabel = (obj: { [k: string]: JsonValue }): string | null => {
    for (const k of LABEL_KEYS) {
        const val = obj[k];
        if (typeof val === 'string' && val.length > 0) {
            return val.length > 40 ? val.slice(0, 40) + '…' : val;
        }
        if (typeof val === 'number') return String(val);
    }
    return null;
};

/** Summarise a value for display in the list row. */
const summarise = (v: JsonValue): string => {
    if (v === null) return 'null';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (typeof v === 'number') return String(v);
    if (typeof v === 'string') return v.length > 60 ? v.slice(0, 60) + '…' : v;
    if (Array.isArray(v)) return `${v.length} item${v.length !== 1 ? 's' : ''}`;
    if (typeof v === 'object') {
        const keys = Object.keys(v);
        const count = `${keys.length} field${keys.length !== 1 ? 's' : ''}`;
        const label = objectLabel(v);
        return label ? `${label}  ·  ${count}` : count;
    }
    return String(v);
};

/** Rename a key inside an object (preserving order). */
const renameKey = (obj: { [k: string]: JsonValue }, oldKey: string, newKey: string): { [k: string]: JsonValue } => {
    const result: { [k: string]: JsonValue } = {};
    for (const k of Object.keys(obj)) {
        result[k === oldKey ? newKey : k] = obj[k];
    }
    return result;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Breadcrumb navigation bar */
const Breadcrumbs: React.FC<{
    segments: BreadcrumbSegment[];
    onNavigate: (path: (string | number)[]) => void;
}> = ({ segments, onNavigate }) => (
    <div
        className="flex items-center gap-1 px-3 py-2 text-xs overflow-x-auto flex-shrink-0"
        style={{ borderBottom: '1px solid var(--border-default)', backgroundColor: 'var(--surface-50)' }}
    >
        <button
            className="flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors hover:bg-[var(--bg-hover)]"
            style={{ color: 'var(--accent)', cursor: 'pointer', background: 'none', border: 'none' }}
            onClick={() => onNavigate([])}
        >
            <Home size={12} />
            <span className="font-medium">Root</span>
        </button>
        {segments.map((seg, i) => (
            <React.Fragment key={i}>
                <ChevronRight size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                {i < segments.length - 1 ? (
                    <button
                        className="px-1.5 py-0.5 rounded transition-colors hover:bg-[var(--bg-hover)]"
                        style={{ color: 'var(--accent)', cursor: 'pointer', background: 'none', border: 'none' }}
                        onClick={() => onNavigate(seg.path)}
                    >
                        {seg.label}
                    </button>
                ) : (
                    <span className="font-semibold px-1.5 py-0.5" style={{ color: 'var(--text-primary)' }}>
                        {seg.label}
                    </span>
                )}
            </React.Fragment>
        ))}
    </div>
);

/** Type selector dropdown */
const TypePicker: React.FC<{
    value: JsonType;
    onChange: (t: JsonType) => void;
    disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
    <select
        className="input-base text-xs px-1.5 py-1 flex-shrink-0"
        style={{ width: 90, minWidth: 90 }}
        value={value}
        onChange={e => onChange(e.target.value as JsonType)}
        disabled={disabled}
    >
        <option value="string">String</option>
        <option value="number">Number</option>
        <option value="boolean">Boolean</option>
        <option value="null">Null</option>
        <option value="object">Object</option>
        <option value="array">Array</option>
    </select>
);

/** Inline value editor for primitives */
const PrimitiveEditor: React.FC<{
    type: JsonType;
    value: JsonValue;
    onChange: (v: JsonValue) => void;
    onKeyDown?: React.KeyboardEventHandler;
    autoFocus?: boolean;
}> = ({ type, value, onChange, onKeyDown, autoFocus }) => {
    if (type === 'boolean') {
        return (
            <button
                className="input-base text-sm px-2 py-1 text-left"
                onClick={() => onChange(!value)}
                onKeyDown={onKeyDown}
                style={{ minWidth: 60 }}
            >
                {value ? 'true' : 'false'}
            </button>
        );
    }
    if (type === 'null') {
        return <span className="text-sm italic px-2" style={{ color: 'var(--text-tertiary)' }}>null</span>;
    }
    return (
        <input
            className="input-base text-sm w-full"
            type={type === 'number' ? 'number' : 'text'}
            value={value == null ? '' : String(value)}
            onChange={e => {
                if (type === 'number') {
                    const n = parseFloat(e.target.value);
                    onChange(isNaN(n) ? 0 : n);
                } else {
                    onChange(e.target.value);
                }
            }}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
        />
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const JsonSettingsEditor = (props: JsonSettingsEditorProps) => {
    const { visualizeOnly, style, className, settings, editable, onDataChange, onPendingChange } = props;

    // Root data state
    const [root, setRoot] = useState<JsonValue>(null);
    // Navigation path — each entry is a key (string) or index (number)
    const [path, setPath] = useState<(string | number)[]>([]);
    // Animation direction
    const [animDir, setAnimDir] = useState<'left' | 'right' | null>(null);
    // Editing state
    const [editingKey, setEditingKey] = useState<string | number | null>(null);
    const [editValues, setEditValues] = useState<{ key: string; value: JsonValue; type: JsonType }>({ key: '', value: '', type: 'string' });
    // Adding state
    const [isAdding, setIsAdding] = useState(false);
    const [newValues, setNewValues] = useState<{ key: string; value: JsonValue; type: JsonType }>({ key: '', value: '', type: 'string' });

    const animTimerRef = useRef<ReturnType<typeof setTimeout>>();

    // Parse settings string on mount / change
    useEffect(() => {
        try {
            const parsed = settings ? JSON.parse(settings) : {};
            setRoot(parsed);
        } catch {
            setRoot({});
        }
    }, [settings]);

    // Propagate changes upward
    const updateRoot = useCallback((newRoot: JsonValue) => {
        setRoot(newRoot);
        onDataChange(newRoot);
    }, [onDataChange]);

    // Current value at the navigation path
    const current = getAtPath(root, path);
    const currentType = detectType(current);
    const isObject = currentType === 'object';
    const isArray = currentType === 'array';

    // Build entries for the current level
    const entries: JsonEntry[] = React.useMemo(() => {
        if (isObject && current !== null) {
            return Object.entries(current as { [k: string]: JsonValue }).map(([k, v]) => ({
                key: k,
                value: v,
                type: detectType(v),
            }));
        }
        if (isArray) {
            return (current as JsonValue[]).map((v, i) => ({
                key: String(i),
                value: v,
                type: detectType(v),
            }));
        }
        return [];
    }, [current, isObject, isArray]);

    // Build breadcrumbs
    const breadcrumbs: BreadcrumbSegment[] = React.useMemo(() => {
        const segs: BreadcrumbSegment[] = [];
        for (let i = 0; i < path.length; i++) {
            segs.push({
                label: String(path[i]),
                path: path.slice(0, i + 1),
            });
        }
        return segs;
    }, [path]);

    // Navigation
    const navigateTo = (newPath: (string | number)[]) => {
        const target = getAtPath(root, newPath);
        const tt = detectType(target);
        if (tt !== 'object' && tt !== 'array') return; // can only drill into containers

        setEditingKey(null);
        setIsAdding(false);

        if (newPath.length > path.length) {
            setAnimDir('left');
        } else {
            setAnimDir('right');
        }
        setPath(newPath);

        clearTimeout(animTimerRef.current);
        animTimerRef.current = setTimeout(() => setAnimDir(null), 250);
    };

    const drillInto = (key: string | number) => {
        navigateTo([...path, key]);
    };

    // CRUD operations
    const handleAdd = () => {
        if (!isObject && !isArray) return;

        let newRoot: JsonValue;
        if (isArray) {
            const arr = [...(current as JsonValue[]), newValues.value];
            newRoot = path.length === 0 ? arr : setAtPath(root, path, arr);
        } else {
            const key = newValues.key.trim();
            if (!key) return;
            if ((current as any)[key] !== undefined) return; // duplicate key
            const obj = { ...(current as { [k: string]: JsonValue }), [key]: newValues.value };
            newRoot = path.length === 0 ? obj : setAtPath(root, path, obj);
        }

        updateRoot(newRoot);
        setNewValues({ key: '', value: '', type: 'string' });
        setIsAdding(false);
    };

    const handleDelete = (key: string | number) => {
        const actualKey = isArray ? Number(key) : key;
        const newRoot = deleteAtPath(root, path, actualKey);
        updateRoot(newRoot);
        if (editingKey === key) setEditingKey(null);
    };

    const handleStartEdit = (entry: JsonEntry) => {
        setEditingKey(entry.key);
        setEditValues({ key: entry.key, value: entry.value, type: entry.type });
        setIsAdding(false);
    };

    const handleSaveEdit = () => {
        if (editingKey === null) return;

        const isPrimitive = editValues.type !== 'object' && editValues.type !== 'array';
        let value = editValues.value;

        // If type changed, reset value to default
        const oldEntry = entries.find(e => e.key === editingKey);
        if (oldEntry && oldEntry.type !== editValues.type) {
            if (isPrimitive) {
                // Keep the edited value
            } else {
                value = defaultForType(editValues.type);
            }
        }

        let newCurrent: JsonValue;
        if (isArray) {
            const arr = [...(current as JsonValue[])];
            arr[Number(editingKey)] = value;
            newCurrent = arr;
        } else {
            const obj = current as { [k: string]: JsonValue };
            const newKey = editValues.key.trim() || String(editingKey);
            if (newKey !== editingKey) {
                newCurrent = renameKey(obj, String(editingKey), newKey);
                (newCurrent as any)[newKey] = value;
            } else {
                newCurrent = { ...obj, [String(editingKey)]: value };
            }
        }

        const newRoot = path.length === 0 ? newCurrent : setAtPath(root, path, newCurrent);
        updateRoot(newRoot);
        setEditingKey(null);
    };

    const handleCancelEdit = () => setEditingKey(null);
    const handleCancelAdd = () => {
        setIsAdding(false);
        setNewValues({ key: '', value: '', type: 'string' });
    };

    const handleKeyDown = (e: React.KeyboardEvent, action: 'add' | 'edit') => {
        if (e.key === 'Enter') {
            e.preventDefault();
            action === 'add' ? handleAdd() : handleSaveEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            action === 'add' ? handleCancelAdd() : handleCancelEdit();
        }
    };

    const handleNewTypeChange = (t: JsonType) => {
        setNewValues(prev => ({ ...prev, type: t, value: defaultForType(t) }));
    };

    const handleEditTypeChange = (t: JsonType) => {
        setEditValues(prev => ({ ...prev, type: t, value: defaultForType(t) }));
    };

    // -----------------------------------------------------------------------
    // View mode toggle & raw JSON
    // -----------------------------------------------------------------------
    const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');
    const [rawJson, setRawJson] = useState('');
    const [rawJsonError, setRawJsonError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);

    useEffect(() => {
        const hasPendingChange = isAdding || editingKey !== null || (viewMode === 'raw' && rawJsonError !== null);
        onPendingChange?.(hasPendingChange);
    }, [isAdding, editingKey, viewMode, rawJsonError, onPendingChange]);

    // Sync rawJson when switching to raw mode or when root changes while in raw mode
    useEffect(() => {
        if (viewMode === 'raw') {
            setRawJson(JSON.stringify(root, null, 2));
            setRawJsonError(null);
        }
    }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRawJsonChange = (text: string) => {
        setRawJson(text);
        try {
            const parsed = JSON.parse(text);
            setRawJsonError(null);
            setRoot(parsed);
            onDataChange(parsed);
        } catch (e: any) {
            setRawJsonError(e.message || 'Invalid JSON');
        }
    };

    const handleCopyToClipboard = async () => {
        try {
            const text = JSON.stringify(root, null, 2);
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
            // Fallback for older browsers
            const text = JSON.stringify(root, null, 2);
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const parsed = JSON.parse(text);
            updateRoot(parsed);
            setRawJson(JSON.stringify(parsed, null, 2));
            setRawJsonError(null);
            setPath([]);
        } catch (e: any) {
            setRawJsonError(e.message || 'Invalid JSON in clipboard');
        }
    };

    /** Shared toolbar for switching between structured ↔ raw view + copy/paste */
    const ModeToolbar: React.FC<{ showAddButton?: boolean }> = ({ showAddButton }) => (
        <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: 'var(--border-default)' }}>
            {/* Add button (editable structured mode only) */}
            {showAddButton && viewMode === 'structured' && (
                <button
                    className="btn btn-ghost text-sm"
                    onClick={() => { setIsAdding(true); setEditingKey(null); }}
                    disabled={isAdding}
                >
                    <Plus size={15} /> Add {isArray ? 'Item' : 'Field'}
                </button>
            )}

            <div className="flex items-center gap-1 ml-auto">
                {/* Copy */}
                <button
                    className="btn-icon p-1.5 rounded transition-colors hover:bg-[var(--bg-hover)]"
                    onClick={handleCopyToClipboard}
                    title="Copy JSON to clipboard"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: copySuccess ? 'var(--success)' : 'var(--text-tertiary)' }}
                >
                    {copySuccess ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                </button>

                {/* Paste (editable only) */}
                {editable && (
                    <button
                        className="btn-icon p-1.5 rounded transition-colors hover:bg-[var(--bg-hover)]"
                        onClick={handlePasteFromClipboard}
                        title="Paste JSON from clipboard"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
                    >
                        <ClipboardPaste size={15} />
                    </button>
                )}

                {/* Separator */}
                <div className="w-px h-4 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />

                {/* Toggle buttons */}
                <div
                    className="flex items-center rounded-md overflow-hidden"
                    style={{ border: '1px solid var(--border-default)' }}
                >
                    <button
                        className="flex items-center gap-1 px-2 py-1 text-xs transition-colors"
                        style={{
                            background: viewMode === 'structured' ? 'var(--accent-soft)' : 'transparent',
                            color: viewMode === 'structured' ? 'var(--accent-text)' : 'var(--text-tertiary)',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: viewMode === 'structured' ? 600 : 400,
                        }}
                        onClick={() => setViewMode('structured')}
                        title="Structured view"
                    >
                        <List size={13} />
                        <span>Fields</span>
                    </button>
                    <button
                        className="flex items-center gap-1 px-2 py-1 text-xs transition-colors"
                        style={{
                            background: viewMode === 'raw' ? 'var(--accent-soft)' : 'transparent',
                            color: viewMode === 'raw' ? 'var(--accent-text)' : 'var(--text-tertiary)',
                            border: 'none',
                            borderLeft: '1px solid var(--border-default)',
                            cursor: 'pointer',
                            fontWeight: viewMode === 'raw' ? 600 : 400,
                        }}
                        onClick={() => setViewMode('raw')}
                        title="Raw JSON"
                    >
                        <Code size={13} />
                        <span>JSON</span>
                    </button>
                </div>

                {/* Level indicator (structured mode only) */}
                {viewMode === 'structured' && (
                    <span className="text-xs ml-2" style={{ color: 'var(--text-tertiary)' }}>
                        {isArray ? `Array · ${entries.length} items` : `Object · ${entries.length} fields`}
                    </span>
                )}
            </div>
        </div>
    );

    /** Raw JSON editor / viewer panel */
    const RawJsonPanel: React.FC<{ readOnly?: boolean }> = ({ readOnly }) => (
        <div className="flex flex-col flex-1 overflow-hidden">
            {rawJsonError && (
                <div
                    className="flex items-center gap-2 px-3 py-2 text-xs"
                    style={{
                        backgroundColor: 'var(--danger-soft)',
                        color: 'var(--danger)',
                        borderBottom: '1px solid var(--danger)',
                    }}
                >
                    <X size={12} />
                    {rawJsonError}
                </div>
            )}
            <textarea
                className="flex-1 w-full p-3 text-sm resize-none focus:outline-none"
                style={{
                    fontFamily: 'var(--font-mono, "JetBrains Mono", "Fira Code", monospace)',
                    backgroundColor: 'var(--surface-50)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    tabSize: 2,
                    lineHeight: 1.6,
                    minHeight: '200px',
                }}
                value={rawJson}
                onChange={e => handleRawJsonChange(e.target.value)}
                readOnly={readOnly}
                spellCheck={false}
                placeholder='{"key": "value"}'
            />
        </div>
    );

    // -----------------------------------------------------------------------
    // Visualize-only mode
    // -----------------------------------------------------------------------
    if (visualizeOnly) {
        return (
            <div className={`${styles.JsonSettingsEditor} ${className || ''}`} style={style} data-testid="JsonSettingsEditor">
                <div className="flex flex-col h-full">
                    <ModeToolbar />
                    {viewMode === 'structured' && path.length > 0 && <Breadcrumbs segments={breadcrumbs} onNavigate={navigateTo} />}

                    {viewMode === 'raw' ? (
                        <RawJsonPanel readOnly />
                    ) : (
                        <div
                            className={`flex-1 overflow-auto ${animDir === 'left' ? 'animate-slide-left' : animDir === 'right' ? 'animate-slide-right' : ''}`}
                            style={{ maxHeight: style?.maxHeight || style?.height || '500px' }}
                        >
                            {entries.length === 0 ? (
                                <div className="flex items-center justify-center h-full py-8" style={{ color: 'var(--text-tertiary)' }}>
                                    <span className="text-sm">No settings configured</span>
                                </div>
                            ) : (
                                <div className="divide-y" style={{ borderColor: 'var(--border-default)' }}>
                                    {entries.map((entry) => {
                                        const isContainer = entry.type === 'object' || entry.type === 'array';
                                        return (
                                            <div
                                                key={entry.key}
                                                className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${isContainer ? 'cursor-pointer hover:bg-[var(--bg-hover)]' : ''}`}
                                                onClick={() => isContainer && drillInto(isArray ? Number(entry.key) : entry.key)}
                                            >
                                                <span style={{ color: typeBadgeColor(entry.type) }}>{typeIcon(entry.type)}</span>
                                                <span className="font-medium text-sm min-w-[100px]" style={{ color: 'var(--text-primary)' }}>
                                                    {isArray ? `[${entry.key}]` : entry.key}
                                                </span>
                                                <span className="text-sm flex-1 truncate" style={{ color: isContainer ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                                                    {summarise(entry.value)}
                                                </span>
                                                {isContainer && <ChevronRight size={14} style={{ color: 'var(--text-tertiary)' }} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Editable mode
    // -----------------------------------------------------------------------

    const isPrimitiveNew = newValues.type !== 'object' && newValues.type !== 'array';
    const isPrimitiveEdit = editValues.type !== 'object' && editValues.type !== 'array';

    return (
        <div className={`${styles.JsonSettingsEditor} ${className || ''}`} style={style} data-testid="JsonSettingsEditor">
            <div className="flex flex-col h-full">
                {/* Toolbar */}
                <ModeToolbar showAddButton={editable} />

                {/* Breadcrumbs */}
                {viewMode === 'structured' && path.length > 0 && <Breadcrumbs segments={breadcrumbs} onNavigate={navigateTo} />}

                {/* Raw JSON mode */}
                {viewMode === 'raw' ? (
                    <RawJsonPanel readOnly={!editable} />
                ) : (
                /* Entries list */
                <div
                    className={`flex-1 overflow-auto ${animDir === 'left' ? 'animate-slide-left' : animDir === 'right' ? 'animate-slide-right' : ''}`}
                >
                    {/* Add row */}
                    {isAdding && (
                        <div
                            className="flex items-center gap-2 px-4 py-2.5"
                            style={{ borderBottom: '1px solid var(--border-default)', backgroundColor: 'var(--surface-secondary, var(--surface-50))' }}
                        >
                            {/* Key (only for objects) */}
                            {isObject && (
                                <input
                                    className="input-base text-sm flex-shrink-0"
                                    style={{ width: 130 }}
                                    placeholder="Key"
                                    value={newValues.key}
                                    onChange={e => setNewValues(v => ({ ...v, key: e.target.value }))}
                                    onKeyDown={e => handleKeyDown(e, 'add')}
                                    autoFocus
                                />
                            )}
                            {/* Type picker */}
                            <TypePicker value={newValues.type} onChange={handleNewTypeChange} />
                            {/* Value (primitives only) */}
                            {isPrimitiveNew && (
                                <div className="flex-1 min-w-0">
                                    <PrimitiveEditor
                                        type={newValues.type}
                                        value={newValues.value}
                                        onChange={v => setNewValues(prev => ({ ...prev, value: v }))}
                                        onKeyDown={e => handleKeyDown(e, 'add')}
                                        autoFocus={isArray}
                                    />
                                </div>
                            )}
                            {!isPrimitiveNew && (
                                <span className="text-xs italic flex-1" style={{ color: 'var(--text-tertiary)' }}>
                                    Empty {newValues.type} — drill in to populate
                                </span>
                            )}
                            {/* Actions */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button className="btn-icon p-1 rounded" onClick={handleAdd} title="Confirm">
                                    <Check size={14} style={{ color: 'var(--success)' }} />
                                </button>
                                <button className="btn-icon p-1 rounded" onClick={handleCancelAdd} title="Cancel">
                                    <X size={14} style={{ color: 'var(--text-tertiary)' }} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Data rows */}
                    {entries.length === 0 && !isAdding ? (
                        <div className="flex items-center justify-center py-8" style={{ color: 'var(--text-tertiary)' }}>
                            <span className="text-sm">
                                {editable
                                    ? `No ${isArray ? 'items' : 'fields'} yet. Click "Add ${isArray ? 'Item' : 'Field'}" to create one.`
                                    : 'No settings configured'}
                            </span>
                        </div>
                    ) : (
                        <div className="divide-y" style={{ borderColor: 'var(--border-default)' }}>
                            {entries.map((entry) => {
                                const isContainer = entry.type === 'object' || entry.type === 'array';
                                const isEditing = editingKey === entry.key;

                                if (isEditing) {
                                    return (
                                        <div
                                            key={entry.key}
                                            className="flex items-center gap-2 px-4 py-2.5"
                                            style={{ backgroundColor: 'var(--surface-secondary, var(--surface-50))' }}
                                        >
                                            {/* Key (objects only) */}
                                            {isObject && (
                                                <input
                                                    className="input-base text-sm flex-shrink-0"
                                                    style={{ width: 130 }}
                                                    value={editValues.key}
                                                    onChange={e => setEditValues(v => ({ ...v, key: e.target.value }))}
                                                    onKeyDown={e => handleKeyDown(e, 'edit')}
                                                    autoFocus
                                                />
                                            )}
                                            {isArray && (
                                                <span className="text-sm font-mono px-1 flex-shrink-0" style={{ color: 'var(--text-tertiary)', minWidth: 40 }}>
                                                    [{entry.key}]
                                                </span>
                                            )}
                                            {/* Type picker */}
                                            <TypePicker value={editValues.type} onChange={handleEditTypeChange} />
                                            {/* Value */}
                                            {isPrimitiveEdit && (
                                                <div className="flex-1 min-w-0">
                                                    <PrimitiveEditor
                                                        type={editValues.type}
                                                        value={editValues.value}
                                                        onChange={v => setEditValues(prev => ({ ...prev, value: v }))}
                                                        onKeyDown={e => handleKeyDown(e, 'edit')}
                                                    />
                                                </div>
                                            )}
                                            {!isPrimitiveEdit && (
                                                <span className="text-xs italic flex-1" style={{ color: 'var(--text-tertiary)' }}>
                                                    {editValues.type === editValues.type ? summarise(editValues.value) : `Empty ${editValues.type}`}
                                                </span>
                                            )}
                                            {/* Actions */}
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <button className="btn-icon p-1 rounded" onClick={handleSaveEdit} title="Save">
                                                    <Check size={14} style={{ color: 'var(--success)' }} />
                                                </button>
                                                <button className="btn-icon p-1 rounded" onClick={handleCancelEdit} title="Cancel">
                                                    <X size={14} style={{ color: 'var(--text-tertiary)' }} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div
                                        key={entry.key}
                                        className={`group flex items-center gap-3 px-4 py-2.5 transition-colors ${isContainer ? 'cursor-pointer hover:bg-[var(--bg-hover)]' : ''}`}
                                        onClick={() => isContainer && drillInto(isArray ? Number(entry.key) : entry.key)}
                                    >
                                        {/* Type icon */}
                                        <span style={{ color: typeBadgeColor(entry.type), flexShrink: 0 }}>
                                            {typeIcon(entry.type)}
                                        </span>
                                        {/* Key / index */}
                                        <span className="font-medium text-sm" style={{ color: 'var(--text-primary)', minWidth: 100 }}>
                                            {isArray ? `[${entry.key}]` : entry.key}
                                        </span>
                                        {/* Value summary */}
                                        <span className="text-sm flex-1 truncate" style={{ color: isContainer ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                                            {summarise(entry.value)}
                                        </span>
                                        {/* Row actions */}
                                        {editable && (
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                                <button
                                                    className="btn-icon p-1 rounded"
                                                    onClick={(e) => { e.stopPropagation(); handleStartEdit(entry); }}
                                                    title="Edit"
                                                >
                                                    <Pencil size={14} style={{ color: 'var(--text-secondary)' }} />
                                                </button>
                                                <button
                                                    className="btn-icon p-1 rounded"
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(isArray ? Number(entry.key) : entry.key); }}
                                                    title="Remove"
                                                >
                                                    <Trash2 size={14} style={{ color: 'var(--danger)' }} />
                                                </button>
                                            </div>
                                        )}
                                        {/* Drill-in chevron */}
                                        {isContainer && <ChevronRight size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                )}
            </div>
        </div>
    );
};

export default JsonSettingsEditor;
